import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { PlatformProvider, usePlatform } from "./context/PlatformContext";
import { ProtectedRoute, AdminRoute, ArtistRoute, AgentRoute } from "./components/ProtectedRoute";
import { GateGuard } from "./components/GateGuard";
import { TestModeBanner } from "./components/TestModeBanner";
import { EarlyAccessBanner } from "./components/EarlyAccessBanner";
import ScrollToTop from "./components/ScrollToTop";
import ComingSoon from "./pages/ComingSoon";
import Landing from "./pages/Landing";
import BrowseArtists from "./pages/BrowseArtists";
import WeeklySpecials from "./pages/WeeklySpecials";
import ArtistProfile from "./pages/ArtistProfile";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import ArtistDashboard from "./pages/ArtistDashboard";
import Studio from "./pages/Studio";
import AgentDashboard from "./pages/AgentDashboard";
import ArtistSetup from "./pages/ArtistSetup";
import AdminPanel from "./pages/AdminPanel";
import TwoFactorSetup from "./pages/TwoFactorSetup";
import FAQ from "./pages/FAQ";
import OrderSuccess from "./pages/OrderSuccess";
import OrderCancel from "./pages/OrderCancel";
import Checkout from "./pages/Checkout";
import Cart from "./pages/Cart";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import OfferSuccess from "./pages/OfferSuccess";
import About from "./pages/About";
import VerifyEmail from "./pages/VerifyEmail";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Subprocessors from "./pages/Subprocessors";
import { EmailVerificationBanner } from "./components/EmailVerificationBanner";
import "./App.css";

import Settings from './pages/Settings';

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <PlatformProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AppShell />
          </BrowserRouter>
        </PlatformProvider>
      </CartProvider>
    </AuthProvider>
  );
}

function AppShell() {
  const { testMode } = useAuth();
  const { ordersEnabled, loading: platformLoading } = usePlatform();
  const location = useLocation();
  const earlyAccessActive = !platformLoading && !ordersEnabled && location.pathname !== '/';
  const bannerCount = (testMode?.active ? 1 : 0) + (earlyAccessActive ? 1 : 0);
  const padClass = bannerCount === 2 ? 'pt-20' : bannerCount === 1 ? 'pt-10' : '';
  return (
    <div className={`min-h-screen bg-[#050505] ${padClass}`}>
      <div className="noise-overlay" />
      <TestModeBanner />
      <EarlyAccessBanner />
      <Routes>
              {/* Coming Soon gate — no navbar, no chrome */}
              <Route path="/" element={<ComingSoon />} />

              {/* Public legal pages — always accessible, no gate. Required so
                  visitors can read Privacy + Terms BEFORE signing up. */}
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/legal/subprocessors" element={<Subprocessors />} />

              {/* All app routes require access code */}
              <Route path="/home" element={<GateGuard><EmailVerificationBanner /><Landing /></GateGuard>} />
              <Route path="/artists" element={<GateGuard><EmailVerificationBanner /><BrowseArtists /></GateGuard>} />
              <Route path="/weekly-specials" element={<GateGuard><EmailVerificationBanner /><WeeklySpecials /></GateGuard>} />
              <Route path="/artists/:id" element={<GateGuard><EmailVerificationBanner /><ArtistProfile /></GateGuard>} />
              <Route path="/artist/:id" element={<GateGuard><EmailVerificationBanner /><ArtistProfile /></GateGuard>} />
              <Route path="/login" element={<GateGuard><Login /></GateGuard>} />
              <Route path="/register" element={<GateGuard><Register /></GateGuard>} />
              <Route path="/forgot-password" element={<GateGuard><ForgotPassword /></GateGuard>} />
              <Route path="/reset-password" element={<GateGuard><ResetPassword /></GateGuard>} />
              <Route path="/verify-email" element={<GateGuard><VerifyEmail /></GateGuard>} />
              <Route path="/about" element={<GateGuard><EmailVerificationBanner /><About /></GateGuard>} />
              <Route path="/faq" element={<GateGuard><EmailVerificationBanner /><FAQ /></GateGuard>} />
              <Route path="/artist-setup" element={<GateGuard><ArtistSetup /></GateGuard>} />
              <Route path="/cart" element={
                <GateGuard><ProtectedRoute><EmailVerificationBanner /><Cart /></ProtectedRoute></GateGuard>
              } />
              <Route path="/checkout/success" element={
                <GateGuard><ProtectedRoute><CheckoutSuccess /></ProtectedRoute></GateGuard>
              } />
              <Route path="/checkout/:artistId" element={
                <GateGuard><ProtectedRoute><EmailVerificationBanner /><Checkout /></ProtectedRoute></GateGuard>
              } />
              <Route path="/dashboard" element={
                <GateGuard><ProtectedRoute><EmailVerificationBanner /><Dashboard /></ProtectedRoute></GateGuard>
              } />
              <Route path="/artist-dashboard" element={
                <GateGuard><ArtistRoute><EmailVerificationBanner /><ArtistDashboard /></ArtistRoute></GateGuard>
              } />
              <Route path="/studio" element={
                <GateGuard><ArtistRoute><Studio /></ArtistRoute></GateGuard>
              } />
              <Route path="/agent-dashboard" element={
                <GateGuard><AgentRoute><EmailVerificationBanner /><AgentDashboard /></AgentRoute></GateGuard>
              } />
              <Route path="/admin" element={
                <GateGuard><AdminRoute><AdminPanel /></AdminRoute></GateGuard>
              } />
              {/* GDPR support console — deep-link into the AdminPanel privacy tab. */}
              <Route path="/admin/privacy-requests" element={<Navigate to="/admin?tab=privacy" replace />} />
              <Route path="/admin/2fa-setup" element={
                <GateGuard><AdminRoute><TwoFactorSetup /></AdminRoute></GateGuard>
              } />
              <Route path="/settings" element={
                <GateGuard><ProtectedRoute><EmailVerificationBanner /><Settings /></ProtectedRoute></GateGuard>
              } />
              <Route path="/order/success" element={<GateGuard><OrderSuccess /></GateGuard>} />
              <Route path="/order/cancel" element={<GateGuard><OrderCancel /></GateGuard>} />
              <Route path="/offer/success" element={
                <GateGuard><ProtectedRoute><OfferSuccess /></ProtectedRoute></GateGuard>
              } />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster 
              position="top-right" 
              toastOptions={{
                style: {
                  background: '#1E1E1E',
                  border: '1px solid #333',
                  color: '#EDEDED',
                },
              }}
            />
          </div>
  );
}

export default App;
